/**
 * Pressure Testing bundle 2 dialogs (fieldiq-api#915): New test job, Add / edit
 * test (with the inline "New test type…"), and Cancel job.
 *
 * PRESSURES GO TO THE API IN THE TENANT'S UNIT, AS TYPED — the same rule as
 * PtDialogs.tsx. The API converts to kPa; nothing here converts.
 *
 * THE CALIBRATION GUARD IS THE API'S. The instrument pickers list every gauge
 * and recorder with its status, and disable the ones whose record already says
 * the guard will refuse them (`ptInstrumentBlockReason`), with the reason in the
 * option. That is a courtesy. When the API refuses anyway — a calibration that
 * lapsed at midnight, a status changed in another tab — its 400 names the
 * `field` and lists every failure in `errors`, and each message is shown inline
 * at that field, as sent. A refusal is never swallowed into a toast.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import {
  pressureTestingKeys,
  useCreatePtJob,
  useCreatePtTest,
  useCreatePtTestType,
  usePtCustomerSettings,
  usePtInstruments,
  usePtTestTypes,
  useUpdatePtJob,
  useUpdatePtTest,
} from '../../hooks/usePressureTesting';
import { unwrapArray } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import {
  canAdminPt,
  pressureFromInput,
  pressureInputValue,
  ptInstrumentBlockReason,
  ptStatusSkin,
  PT_RESULT_SKIN,
  PT_TICKET_MODE_LABEL,
} from '../../lib/pressure-testing';
import {
  PT_TEST_RESULTS,
  PT_TICKET_MODES,
  type PtTicketMode,
  type PtGuardFailure,
  type PtInstrument,
  type PtJob,
  type PtPressureUnit,
  type PtTest,
  type PtTestResult,
  type PtTestType,
  type PtTestWrite,
} from '../../types/pressure-testing';
import { PT_INPUT, PT_SECONDARY, PtDialog, PtField } from './PtBits';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';

/** The parts of a PT 400 a form acts on. Everything is optional: a dropped connection has none of it. */
export interface PtWriteError {
  message: string;
  code: string | null;
  field: string | null;
  errors: PtGuardFailure[];
  pendingTests: { id: string; test_type_name: string; number: number }[] | null;
}

export function ptWriteError(err: unknown, fallback: string): PtWriteError {
  const body = (err as { response?: { data?: Record<string, unknown> } })?.response?.data ?? {};
  const message = typeof body.error === 'string' && body.error ? body.error : fallback;
  const errors = Array.isArray(body.errors)
    ? (body.errors as unknown[]).filter(
        (e): e is PtGuardFailure =>
          !!e && typeof e === 'object' && typeof (e as PtGuardFailure).error === 'string' && typeof (e as PtGuardFailure).field === 'string',
      )
    : [];
  return {
    message,
    code: typeof body.code === 'string' ? body.code : null,
    field: typeof body.field === 'string' ? body.field : null,
    errors,
    pendingTests: Array.isArray(body.pending_tests) ? (body.pending_tests as PtWriteError['pendingTests']) : null,
  };
}

export function FormError({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

/** An API refusal shown under the field it names. Ink is theme text; the wash and the edge carry the red. */
export function FieldError({ messages, testId }: { messages: string[]; testId?: string }) {
  if (!messages.length) return null;
  return (
    <div role="alert" data-testid={testId} className="mt-1 space-y-1 border-l-2 border-fi-signal-red bg-fi-signal-red/10 px-2 py-1 text-xs text-fieldiq-text">
      {messages.map((m) => (
        <p key={m}>{m}</p>
      ))}
    </div>
  );
}

/** A whole number, null when blank, NaN when not a whole number. */
export function intFromInput(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : NaN;
}

const blankToNull = (s: string) => (s.trim() ? s.trim() : null);

// ─── People and customers (read-only; the shared tables) ─────────────────────

interface Person {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  role?: string;
}
const personName = (u: Person) => [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || '—';

export function usePtCustomers(enabled: boolean) {
  return useQuery({
    queryKey: [...pressureTestingKeys.all, 'customers'],
    queryFn: async () => unwrapArray<{ id: string; name: string }>((await api.customers.list()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function usePtContacts(customerId: string) {
  return useQuery({
    queryKey: [...pressureTestingKeys.all, 'contacts', customerId],
    queryFn: async () => unwrapArray<{ id: string; name: string | null; email?: string | null }>(
      (await api.customers.listContacts(customerId)).data,
    ),
    enabled: !!customerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's people, customers excluded — the API refuses a customer-role technician. */
function usePtPeople(enabled: boolean) {
  return useQuery({
    queryKey: [...pressureTestingKeys.all, 'people'],
    queryFn: async () => unwrapArray<Person>((await api.users.list()).data).filter((u) => u.role !== 'customer'),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── New test job ────────────────────────────────────────────────────────────

interface JobValues {
  customer_id: string;
  customer_contact_id: string;
  po_number: string;
  afe_number: string;
  location_name: string;
  uwi: string;
  lsd: string;
  province_state: string;
  scope: string;
  technician_user_id: string;
  scheduled_date: string;
  ticket_mode: string;
}

/**
 * The customer picker is the select every vertical's create form uses over
 * GET /customers (no shared picker component exists in this repo). The
 * contact list is the chosen customer's; changing the customer clears it,
 * because the API refuses a contact that is not that customer's.
 */
export function NewTestJobDialog({
  open,
  onClose,
  today,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** The tenant's day (meta.today), the date the form opens on. */
  today: string | null;
  onCreated: (job: PtJob) => void;
}) {
  const blank = (): JobValues => ({
    customer_id: '', customer_contact_id: '', po_number: '', afe_number: '', location_name: '', uwi: '', lsd: '',
    province_state: '', scope: '', technician_user_id: '', scheduled_date: today ?? '', ticket_mode: '',
  });
  const [v, setV] = useState<JobValues>(blank);
  const [error, setError] = useState<string | null>(null);
  const create = useCreatePtJob();
  const customers = usePtCustomers(open);
  const contacts = usePtContacts(v.customer_id);
  const people = usePtPeople(open);
  // PT-5: the customer's ticket mode (else the company default), to prefill the
  // select. It is a PREFILL: unless the reader changes it, the body omits
  // ticket_mode and the API snapshots the same answer itself.
  const custMode = usePtCustomerSettings(v.customer_id || null);
  const [modeTouched, setModeTouched] = useState(false);
  const prefillMode = custMode.data?.data?.ticket_mode ?? '';

  useEffect(() => {
    if (open) {
      setV(blank());
      setError(null);
      setModeTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!modeTouched) setV((prev) => ({ ...prev, ticket_mode: prefillMode }));
  }, [prefillMode, modeTouched]);

  const set = (k: keyof JobValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (k === 'customer_id') setModeTouched(false);
    if (k === 'ticket_mode') setModeTouched(true);
    setV((prev) => (k === 'customer_id' ? { ...prev, customer_id: value, customer_contact_id: '', ticket_mode: '' } : { ...prev, [k]: value }));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.customer_id) return setError('Choose a customer.');
    create.mutate(
      {
        customer_id: v.customer_id,
        customer_contact_id: v.customer_contact_id || null,
        po_number: blankToNull(v.po_number),
        afe_number: blankToNull(v.afe_number),
        location_name: blankToNull(v.location_name),
        uwi: blankToNull(v.uwi),
        lsd: blankToNull(v.lsd),
        province_state: blankToNull(v.province_state),
        scope: blankToNull(v.scope),
        technician_user_id: v.technician_user_id || null,
        scheduled_date: v.scheduled_date || null,
        ...(modeTouched && v.ticket_mode ? { ticket_mode: v.ticket_mode as PtTicketMode } : {}),
      },
      {
        onSuccess: (res) => {
          toast.success(res.data?.job_number ? `${res.data.job_number} created` : 'Test job created');
          onClose();
          if (res.data) onCreated(res.data);
        },
        onError: (err) => setError(ptWriteError(err, 'The test job was not created.').message),
      },
    );
  };

  const customerList = customers.data ?? [];
  const contactList = contacts.data ?? [];

  return (
    <PtDialog
      open={open}
      title="New test job"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pt-new-job" className={PRIMARY} disabled={create.isPending}>
            {create.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Create job
          </button>
        </>
      }
    >
      <form id="pt-new-job" onSubmit={submit} className="space-y-4" noValidate>
        <FormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PtField
            label="Customer"
            hint={customers.isError ? 'The customer list could not be loaded.' : customers.isSuccess && !customerList.length ? 'There are no customers on this account.' : undefined}
          >
            <select className={PT_INPUT} value={v.customer_id} onChange={set('customer_id')} required data-testid="pt-job-customer">
              <option value="">{customers.isLoading ? 'Loading…' : 'Choose a customer'}</option>
              {customerList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || '—'}
                </option>
              ))}
            </select>
          </PtField>
          <PtField
            label="Contact"
            hint={v.customer_id && contacts.isError ? "This customer's contacts could not be loaded." : undefined}
          >
            <select className={PT_INPUT} value={v.customer_contact_id} onChange={set('customer_contact_id')} disabled={!v.customer_id}>
              <option value="">
                {!v.customer_id ? 'Choose a customer first' : contacts.isLoading ? 'Loading…' : contactList.length ? 'No contact' : 'This customer has no contacts'}
              </option>
              {contactList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.email || '—'}
                </option>
              ))}
            </select>
          </PtField>
          <PtField label="PO number">
            <input className={PT_INPUT} value={v.po_number} onChange={set('po_number')} maxLength={100} />
          </PtField>
          <PtField label="AFE number">
            <input className={PT_INPUT} value={v.afe_number} onChange={set('afe_number')} maxLength={100} />
          </PtField>
          <PtField label="Location">
            <input className={PT_INPUT} value={v.location_name} onChange={set('location_name')} maxLength={255} />
          </PtField>
          <PtField label="UWI">
            <input className={`${PT_INPUT} font-mono`} value={v.uwi} onChange={set('uwi')} maxLength={50} />
          </PtField>
          <PtField label="LSD">
            <input className={`${PT_INPUT} font-mono`} value={v.lsd} onChange={set('lsd')} maxLength={100} />
          </PtField>
          <PtField label="Province / state" hint="A code or name — AB, Alberta, TX.">
            <input className={PT_INPUT} value={v.province_state} onChange={set('province_state')} />
          </PtField>
          <PtField label="Technician" hint={people.isError ? 'The people list could not be loaded.' : undefined}>
            <select className={PT_INPUT} value={v.technician_user_id} onChange={set('technician_user_id')}>
              <option value="">{people.isLoading ? 'Loading…' : 'Not assigned'}</option>
              {(people.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {personName(u)}
                </option>
              ))}
            </select>
          </PtField>
          <PtField label="Date">
            <input type="date" className={PT_INPUT} value={v.scheduled_date} onChange={set('scheduled_date')} />
          </PtField>
          <PtField
            label="Ticket mode"
            hint={
              !v.customer_id
                ? 'Set from the customer once one is chosen.'
                : custMode.isError
                  ? "The customer's ticket mode could not be loaded; the job will take it anyway unless you choose one."
                  : modeTouched
                    ? 'Changed for this job only.'
                    : custMode.data?.data?.source === 'customer'
                      ? "The customer's setting."
                      : custMode.data?.data
                        ? 'The company default (this customer has no setting of its own).'
                        : undefined
            }
          >
            <select className={PT_INPUT} value={v.ticket_mode} onChange={set('ticket_mode')} disabled={!v.customer_id} data-testid="pt-job-ticket-mode">
              {!v.ticket_mode && <option value="">{v.customer_id && custMode.isLoading ? 'Loading…' : '—'}</option>}
              {PT_TICKET_MODES.map((m) => (
                <option key={m} value={m}>
                  {PT_TICKET_MODE_LABEL[m]}
                </option>
              ))}
            </select>
          </PtField>
        </div>
        <PtField label="Scope">
          <textarea className={`${PT_INPUT} h-24 py-2`} value={v.scope} onChange={set('scope')} />
        </PtField>
      </form>
    </PtDialog>
  );
}

// ─── Cancel job ──────────────────────────────────────────────────────────────

/** The API requires a reason; the dialog will not send without one. Cancelled is terminal. */
export function CancelJobDialog({ open, onClose, job }: { open: boolean; onClose: () => void; job: PtJob }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const update = useUpdatePtJob();

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) return setError('A reason is required to cancel a job.');
    update.mutate(
      { id: job.id, body: { status: 'cancelled', cancel_reason: reason.trim() } },
      {
        onSuccess: () => {
          toast.success(`${job.job_number} cancelled`);
          onClose();
        },
        onError: (err) => setError(ptWriteError(err, 'The job was not cancelled.').message),
      },
    );
  };

  return (
    <PtDialog
      open={open}
      title={`Cancel ${job.job_number}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Keep job
          </button>
          <button
            type="submit"
            form="pt-cancel-job"
            disabled={update.isPending}
            className="inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60"
          >
            {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Cancel job
          </button>
        </>
      }
    >
      <form id="pt-cancel-job" onSubmit={submit} className="space-y-4" noValidate>
        <p className="text-sm text-fieldiq-text">
          A cancelled job cannot be reopened, and its tests can no longer be changed.
        </p>
        <FormError message={error} />
        <PtField label="Reason">
          <textarea
            className={`${PT_INPUT} h-24 py-2`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="pt-cancel-reason"
            autoFocus
          />
        </PtField>
      </form>
    </PtDialog>
  );
}

// ─── Add / edit test ─────────────────────────────────────────────────────────

interface TestValues {
  test_type_id: string;
  test_pressure: string;
  hold_minutes: string;
  start_pressure: string;
  end_pressure: string;
  allowable_drop: string;
  result: PtTestResult;
  gauge_equipment_id: string;
  recorder_equipment_id: string;
  notes: string;
}

function valuesFrom(test: PtTest | null): TestValues {
  return {
    test_type_id: test?.test_type_id ?? '',
    test_pressure: pressureInputValue(test?.test_pressure),
    hold_minutes: test?.hold_minutes == null ? '' : String(test.hold_minutes),
    start_pressure: pressureInputValue(test?.start_pressure),
    end_pressure: pressureInputValue(test?.end_pressure),
    allowable_drop: pressureInputValue(test?.allowable_drop),
    result: (PT_TEST_RESULTS as readonly string[]).includes(test?.result ?? '') ? (test!.result as PtTestResult) : 'pending',
    gauge_equipment_id: test?.gauge.equipment_id ?? '',
    recorder_equipment_id: test?.recorder?.equipment_id ?? '',
    notes: test?.notes ?? '',
  };
}

const NEW_TYPE = '__new__';
const GAUGES = { kind: 'gauge' };
const RECORDERS = { kind: 'recorder' };

/**
 * One instrument picker. Every instrument of the kind is listed with its
 * status; one the record already rules out is disabled with the reason in its
 * label. The current value is always an option — a select cannot show a value
 * it has no option for — so an existing test whose gauge has since lapsed
 * still shows that gauge, disabled, rather than silently showing another.
 */
export function InstrumentPicker({
  label,
  kind,
  value,
  onChange,
  instruments,
  loading,
  failed,
  testPressure,
  unit,
  current,
  optional,
  required,
  errors,
  testId,
}: {
  label: string;
  kind: 'gauge' | 'recorder';
  value: string;
  onChange: (id: string) => void;
  instruments: PtInstrument[];
  loading: boolean;
  failed: boolean;
  testPressure: number | null;
  unit: PtPressureUnit | null;
  current: { equipment_id: string; name: string | null } | null;
  optional: boolean;
  required: boolean;
  errors: string[];
  testId: string;
}) {
  const listed = instruments.some((i) => i.id === value);
  const selected = instruments.find((i) => i.id === value) ?? null;
  const reason = selected ? ptInstrumentBlockReason(selected, testPressure, unit) : null;
  const optionLabel = (i: PtInstrument) => {
    const why = ptInstrumentBlockReason(i, testPressure, unit);
    const name = `${i.name || '—'}${i.serial_number ? ` (${i.serial_number})` : ''}`;
    return why ? `${name} — ${why}` : `${name} — ${ptStatusSkin(i.calibration_status).label}`;
  };
  return (
    <div>
      <PtField
        label={required ? `${label} (required by this test type)` : label}
        hint={failed ? `The ${kind} list could not be loaded.` : !loading && !instruments.length ? `There are no ${kind}s in the register.` : undefined}
      >
        <select className={PT_INPUT} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
          <option value="">{loading ? 'Loading…' : optional ? 'None' : `Choose a ${kind}`}</option>
          {value && !listed && current && current.equipment_id === value && (
            <option value={value} disabled>
              {current.name || '—'} — no longer in the register
            </option>
          )}
          {instruments.map((i) => {
            const why = ptInstrumentBlockReason(i, testPressure, unit);
            return (
              <option key={i.id} value={i.id} disabled={why !== null && i.id !== value}>
                {optionLabel(i)}
              </option>
            );
          })}
        </select>
      </PtField>
      {reason && !errors.length && (
        <p className="mt-1 text-xs text-fieldiq-text" data-testid={`${testId}-reason`}>
          {reason} — this {kind} cannot be used on a test.
        </p>
      )}
      <FieldError messages={errors} testId={`${testId}-error`} />
    </div>
  );
}

/**
 * Add or edit a test. On edit only the fields the reader changed are sent: the
 * API re-runs the calibration guard (and re-snapshots the calibration) when a
 * guarded field changes, and an unchanged pressure echoed back from its 0.1
 * rounding is a change to the API.
 *
 * "⊕ New test type…" opens a small form INSIDE this dialog. Everything typed
 * in the test is kept; the new type is selected when it saves. Shown to
 * PT_ADMIN roles only — the API refuses the rest.
 */
export function TestDialog({
  open,
  onClose,
  job,
  test,
  unit,
}: {
  open: boolean;
  onClose: () => void;
  job: PtJob;
  /** Null for Add. */
  test: PtTest | null;
  unit: PtPressureUnit | null;
}) {
  const { user } = useAuth();
  const [v, setV] = useState<TestValues>(() => valuesFrom(test));
  const [initial, setInitial] = useState<TestValues>(() => valuesFrom(test));
  const [error, setError] = useState<PtWriteError | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [newType, setNewType] = useState<null | { name: string; hold: string; drop: string; recorder: boolean }>(null);
  const [typeError, setTypeError] = useState<string | null>(null);

  const types = usePtTestTypes();
  // The caller mounts this dialog only while it is open, so these read on open.
  const gauges = usePtInstruments(GAUGES);
  const recorders = usePtInstruments(RECORDERS);
  const createTest = useCreatePtTest();
  const updateTest = useUpdatePtTest();
  const createType = useCreatePtTestType();
  const canAddType = canAdminPt(user?.role);

  useEffect(() => {
    if (open) {
      const start = valuesFrom(test);
      setV(start);
      setInitial(start);
      setError(null);
      setLocalError(null);
      setNewType(null);
      setTypeError(null);
    }
  }, [open, test]);

  const typeList = useMemo((): Pick<PtTestType, 'id' | 'name' | 'requires_recorder' | 'default_hold_minutes' | 'default_allowable_drop'>[] => {
    const list = types.data?.data ?? [];
    // The test's own type stays an option after it is deactivated (the API allows it on this test).
    if (test && !list.some((t) => t.id === test.test_type_id)) {
      const own = {
        id: test.test_type_id,
        name: test.test_type_name ?? '—',
        requires_recorder: test.requires_recorder,
        default_hold_minutes: null,
        default_allowable_drop: null,
      };
      return [own, ...list];
    }
    return list;
  }, [types.data, test]);
  const chosenType = typeList.find((t) => t.id === v.test_type_id) ?? null;
  const requiresRecorder = !!chosenType?.requires_recorder;
  const pressure = pressureFromInput(v.test_pressure);
  const pressureForPicker = pressure === null || Number.isNaN(pressure) ? null : pressure;

  const set = (k: keyof TestValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setV((prev) => ({ ...prev, [k]: e.target.value }));

  /** A type's defaults fill hold and allowable drop only where nothing is typed yet. */
  const chooseType = (id: string) => {
    if (id === NEW_TYPE) {
      setNewType({ name: '', hold: '', drop: '', recorder: false });
      setTypeError(null);
      return;
    }
    const t = typeList.find((x) => x.id === id);
    setV((prev) => ({
      ...prev,
      test_type_id: id,
      hold_minutes: prev.hold_minutes || (t?.default_hold_minutes != null ? String(t.default_hold_minutes) : ''),
      allowable_drop: prev.allowable_drop || pressureInputValue(t?.default_allowable_drop),
    }));
  };

  const saveType = () => {
    if (!newType) return;
    setTypeError(null);
    if (!newType.name.trim()) return setTypeError('Name the test type.');
    const hold = intFromInput(newType.hold);
    const drop = pressureFromInput(newType.drop);
    if (Number.isNaN(hold)) return setTypeError('Hold minutes must be a whole number.');
    if (Number.isNaN(drop)) return setTypeError('Allowable drop must be a number.');
    createType.mutate(
      { name: newType.name.trim(), default_hold_minutes: hold, default_allowable_drop: drop, requires_recorder: newType.recorder },
      {
        onSuccess: (res) => {
          const t = res.data;
          setNewType(null);
          if (!t) return;
          setV((prev) => ({
            ...prev,
            test_type_id: t.id,
            hold_minutes: prev.hold_minutes || (t.default_hold_minutes != null ? String(t.default_hold_minutes) : ''),
            allowable_drop: prev.allowable_drop || pressureInputValue(t.default_allowable_drop),
          }));
        },
        onError: (err) => setTypeError(ptWriteError(err, 'The test type was not created.').message),
      },
    );
  };

  /** The API's messages for one field: the guard's list, or the single field it named. */
  const errorsFor = (...fields: string[]) => {
    if (!error) return [];
    const listed = error.errors.filter((e) => fields.includes(e.field)).map((e) => e.error);
    if (listed.length) return listed;
    return error.field && fields.includes(error.field) ? [error.message] : [];
  };
  const fieldErrorShown = !!error && (error.errors.length > 0 || (!!error.field && ['gauge_equipment_id', 'recorder_equipment_id', 'test_pressure', 'result', 'test_type_id'].includes(error.field)));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLocalError(null);
    if (!v.test_type_id) return setLocalError('Choose a test type.');
    if (pressure === null) return setLocalError('Test pressure is required.');
    const nums = {
      test_pressure: pressure,
      start_pressure: pressureFromInput(v.start_pressure),
      end_pressure: pressureFromInput(v.end_pressure),
      allowable_drop: pressureFromInput(v.allowable_drop),
    };
    if (Object.values(nums).some((n) => Number.isNaN(n))) return setLocalError('Pressures must be numbers.');
    const hold = intFromInput(v.hold_minutes);
    if (Number.isNaN(hold)) return setLocalError('Hold minutes must be a whole number.');
    if (!v.gauge_equipment_id) return setLocalError('Choose a gauge.');

    const full: PtTestWrite = {
      test_type_id: v.test_type_id,
      ...nums,
      hold_minutes: hold,
      result: v.result,
      gauge_equipment_id: v.gauge_equipment_id,
      recorder_equipment_id: v.recorder_equipment_id || null,
      notes: blankToNull(v.notes),
    };

    const onError = (err: unknown) => setError(ptWriteError(err, 'The test was not saved.'));
    if (!test) {
      // Hold and allowable drop left blank are left out, so the type's defaults apply.
      const body = { ...full };
      if (hold === null) delete body.hold_minutes;
      if (nums.allowable_drop === null) delete body.allowable_drop;
      createTest.mutate(
        { jobId: job.id, body },
        { onSuccess: () => { toast.success('Test added'); onClose(); }, onError },
      );
      return;
    }
    const keyFor: Record<keyof TestValues, keyof PtTestWrite> = {
      test_type_id: 'test_type_id', test_pressure: 'test_pressure', hold_minutes: 'hold_minutes',
      start_pressure: 'start_pressure', end_pressure: 'end_pressure', allowable_drop: 'allowable_drop',
      result: 'result', gauge_equipment_id: 'gauge_equipment_id', recorder_equipment_id: 'recorder_equipment_id', notes: 'notes',
    };
    const body: PtTestWrite = {};
    (Object.keys(keyFor) as (keyof TestValues)[]).forEach((k) => {
      if (v[k] !== initial[k]) (body as Record<string, unknown>)[keyFor[k]] = full[keyFor[k]];
    });
    if (!Object.keys(body).length) {
      onClose();
      return;
    }
    updateTest.mutate(
      { id: test.id, body },
      { onSuccess: () => { toast.success('Test saved'); onClose(); }, onError },
    );
  };

  const pending = createTest.isPending || updateTest.isPending;
  const pu = unit ?? '';

  return (
    <PtDialog
      open={open}
      title={test ? `Edit test — ${test.test_type_name ?? 'test'}` : `Add test — ${job.job_number}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pt-test" className={PRIMARY} disabled={pending || !!newType}>
            {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {test ? 'Save test' : 'Add test'}
          </button>
        </>
      }
    >
      <form id="pt-test" onSubmit={submit} className="space-y-4" noValidate>
        <FormError message={localError} />
        {error && !fieldErrorShown && <FormError message={error.message} />}

        <div>
          <PtField label="Test type" hint={types.isError ? 'The test types could not be loaded.' : undefined}>
            <select
              className={PT_INPUT}
              value={newType ? NEW_TYPE : v.test_type_id}
              onChange={(e) => chooseType(e.target.value)}
              data-testid="pt-test-type"
            >
              <option value="">{types.isLoading ? 'Loading…' : 'Choose a test type'}</option>
              {typeList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.requires_recorder ? ' (chart recorder)' : ''}
                </option>
              ))}
              {canAddType && <option value={NEW_TYPE}>⊕ New test type…</option>}
            </select>
          </PtField>
          <FieldError messages={errorsFor('test_type_id')} />
        </div>

        {newType && (
          <fieldset className="space-y-3 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3" data-testid="pt-new-type">
            <legend className="px-1 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">New test type</legend>
            <FormError message={typeError} />
            <div className="grid gap-3 sm:grid-cols-3">
              <PtField label="Name">
                <input className={PT_INPUT} value={newType.name} onChange={(e) => setNewType({ ...newType, name: e.target.value })} maxLength={100} autoFocus />
              </PtField>
              <PtField label="Default hold (min)">
                <input className={PT_INPUT} inputMode="numeric" value={newType.hold} onChange={(e) => setNewType({ ...newType, hold: e.target.value })} />
              </PtField>
              <PtField label={`Default allowable drop${pu ? ` (${pu})` : ''}`}>
                <input className={PT_INPUT} inputMode="decimal" value={newType.drop} onChange={(e) => setNewType({ ...newType, drop: e.target.value })} />
              </PtField>
            </div>
            <label className="flex items-center gap-2 text-sm text-fieldiq-text">
              <input type="checkbox" checked={newType.recorder} onChange={(e) => setNewType({ ...newType, recorder: e.target.checked })} />
              Requires a chart recorder
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className={PT_SECONDARY} onClick={() => { setNewType(null); setTypeError(null); }}>
                Back
              </button>
              <button type="button" className={PRIMARY} onClick={saveType} disabled={createType.isPending}>
                {createType.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
                Save test type
              </button>
            </div>
          </fieldset>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <PtField label={`Test pressure${pu ? ` (${pu})` : ''}`}>
              <input className={PT_INPUT} inputMode="decimal" value={v.test_pressure} onChange={set('test_pressure')} data-testid="pt-test-pressure" />
            </PtField>
            <FieldError messages={errorsFor('test_pressure')} testId="pt-test-pressure-error" />
          </div>
          <PtField label="Hold (min)" hint={!test && chosenType ? 'Blank uses the type default.' : undefined}>
            <input className={PT_INPUT} inputMode="numeric" value={v.hold_minutes} onChange={set('hold_minutes')} />
          </PtField>
          <PtField label={`Allowable drop${pu ? ` (${pu})` : ''}`}>
            <input className={PT_INPUT} inputMode="decimal" value={v.allowable_drop} onChange={set('allowable_drop')} />
          </PtField>
          <PtField label={`Start pressure${pu ? ` (${pu})` : ''}`}>
            <input className={PT_INPUT} inputMode="decimal" value={v.start_pressure} onChange={set('start_pressure')} />
          </PtField>
          <PtField label={`End pressure${pu ? ` (${pu})` : ''}`}>
            <input className={PT_INPUT} inputMode="decimal" value={v.end_pressure} onChange={set('end_pressure')} />
          </PtField>
          <div>
            <PtField label="Result">
              <select className={PT_INPUT} value={v.result} onChange={set('result')} data-testid="pt-test-result">
                {PT_TEST_RESULTS.map((r) => (
                  <option key={r} value={r}>
                    {PT_RESULT_SKIN[r].label}
                  </option>
                ))}
              </select>
            </PtField>
            <FieldError messages={errorsFor('result')} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <InstrumentPicker
            label="Gauge"
            kind="gauge"
            value={v.gauge_equipment_id}
            onChange={(id) => setV((prev) => ({ ...prev, gauge_equipment_id: id }))}
            instruments={gauges.data?.data ?? []}
            loading={gauges.isLoading}
            failed={gauges.isError}
            testPressure={pressureForPicker}
            unit={unit}
            current={test ? test.gauge : null}
            optional={false}
            required={false}
            errors={errorsFor('gauge_equipment_id')}
            testId="pt-gauge"
          />
          <InstrumentPicker
            label="Chart recorder"
            kind="recorder"
            value={v.recorder_equipment_id}
            onChange={(id) => setV((prev) => ({ ...prev, recorder_equipment_id: id }))}
            instruments={recorders.data?.data ?? []}
            loading={recorders.isLoading}
            failed={recorders.isError}
            testPressure={pressureForPicker}
            unit={unit}
            current={test?.recorder ?? null}
            optional={!requiresRecorder}
            required={requiresRecorder}
            errors={errorsFor('recorder_equipment_id')}
            testId="pt-recorder"
          />
        </div>

        <PtField label="Notes">
          <textarea className={`${PT_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} />
        </PtField>
      </form>
    </PtDialog>
  );
}
